import type {
  ClipboardEventHandler,
  DragEventHandler,
  FormEventHandler,
  ReactNode,
  RefObject,
} from "react";
import { memo } from "react";
import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import type { ResolvedKeybindingsConfig } from "@cmmd-forge/contracts";
import { ComposerPromptEditor, type ComposerPromptEditorHandle } from "../ComposerPromptEditor";
import type { ComposerTriggerKind } from "../../composer-logic";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import type { ComposerCommandItem } from "./ComposerCommandMenu";
import { ComposerCommandMenu } from "./ComposerCommandMenu";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { cn } from "~/lib/utils";
import { ComposerImageTray } from "./ComposerImageTray";
import { ComposerBanner } from "./composer-notices/ComposerBanner";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import { resolveShortcutCommand } from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";

interface ComposerSurfaceMenuProps {
  readonly open: boolean;
  readonly items?: ReadonlyArray<ComposerCommandItem> | undefined;
  readonly resolvedTheme: "dark" | "light";
  readonly isLoading?: boolean | undefined;
  readonly triggerKind?: ComposerTriggerKind | null | undefined;
  readonly groupSlashCommandSections?: boolean | undefined;
  readonly emptyStateText?: string | undefined;
  readonly activeItemId?: string | null | undefined;
  readonly onHighlightedItemChange?: ((itemId: string | null) => void) | undefined;
  readonly onSelect?: ((item: ComposerCommandItem) => void) | undefined;
}

interface ComposerSurfacePromptEditorProps {
  readonly ref?: RefObject<ComposerPromptEditorHandle | null> | undefined;
  readonly value: string;
  readonly cursor: number;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly skills: ReadonlyArray<ServerProviderSkill>;
  readonly onRemoveTerminalContext: (id: string) => void;
  readonly onChange: (
    nextValue: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
    terminalContextIds: string[],
  ) => void;
  readonly onCommandKeyDown: (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
    editorValue: string,
  ) => boolean;
  readonly onPaste?: ClipboardEventHandler<HTMLElement> | undefined;
  readonly placeholder: string;
  readonly disabled?: boolean | undefined;
  readonly className?: string | undefined;
}

interface ComposerSurfaceProps {
  readonly formRef?: RefObject<HTMLFormElement | null> | undefined;
  readonly onSubmit: FormEventHandler<HTMLFormElement>;
  readonly frameClassName?: string | undefined;
  readonly surfaceClassName?: string | undefined;
  readonly isDragOver?: boolean | undefined;
  readonly onDragEnter?: DragEventHandler<HTMLDivElement> | undefined;
  readonly onDragOver?: DragEventHandler<HTMLDivElement> | undefined;
  readonly onDragLeave?: DragEventHandler<HTMLDivElement> | undefined;
  readonly onDrop?: DragEventHandler<HTMLDivElement> | undefined;
  readonly header?: ReactNode | undefined;
  readonly headerClassName?: string | undefined;
  readonly hasHeader?: boolean | undefined;
  readonly menu: ComposerSurfaceMenuProps;
  readonly promptEditor: ComposerSurfacePromptEditorProps;
  readonly images?: ReadonlyArray<ComposerImageAttachment> | undefined;
  readonly files?: ReadonlyArray<ComposerFileAttachment> | undefined;
  readonly nonPersistedImageIds?: ReadonlySet<string> | undefined;
  readonly onExpandImage?: ((preview: ExpandedImagePreview) => void) | undefined;
  readonly onRemoveImage?: ((imageId: string) => void) | undefined;
  readonly onRemoveFile?: ((fileId: string) => void) | undefined;
  readonly footerLeft?: ReactNode | undefined;
  readonly footerLeftClassName?: string | undefined;
  readonly footerRight: ReactNode;
  /** A small tab attached to the composer's upper shoulder. */
  readonly stashShoulder?: ReactNode | undefined;
  readonly hasStashShoulder?: boolean | undefined;
  readonly onStashShortcut?: (() => void) | undefined;
  readonly keybindings?: ResolvedKeybindingsConfig | undefined;
  readonly terminalOpen?: boolean | undefined;
  readonly footerCompact?: boolean | undefined;
  readonly primaryActionsCompact?: boolean | undefined;
  /**
   * Resting Composer: the single-line state an existing thread takes while the
   * person scrolls back through history or leaves the editor. `ChatComposer`
   * decides it and the layout itself lives in `index.css`, keyed on the
   * `data-composer-resting` attribute this prop writes, so the surface
   * advertises all three layout states through one set of attributes.
   */
  readonly resting?: boolean | undefined;
  /**
   * Run context — where the turn will execute — rendered as a strip welded to
   * the bottom of the composer rather than a header above the prompt.
   *
   * Below, not above, because it answers a different question than the prompt:
   * the prompt is what you are asking for, the strip is where it will happen.
   * Putting it above pushed the first thing you read away from the top of the
   * box and made the composer look like a form with a toolbar. Below, the
   * clip-path in `index.css` joins the two into one glass object, so it reads as
   * a footnote on the composer instead of a second control.
   */
  readonly contextStrip?: ReactNode | undefined;
  /**
   * Rendered between the prompt and the footer. Validation lands where the eye
   * already is after a failed send, instead of as a toast that has to be hunted
   * for — and above the footer so it never displaces the send button.
   */
  readonly belowPrompt?: ReactNode | undefined;
}

const EMPTY_IMAGE_ID_SET = new Set<string>();
const EMPTY_IMAGES: ReadonlyArray<ComposerImageAttachment> = [];
const EMPTY_FILES: ReadonlyArray<ComposerFileAttachment> = [];
const noopPaste: ClipboardEventHandler<HTMLElement> = () => {};

export const ComposerSurface = memo(function ComposerSurface({
  formRef,
  onSubmit,
  frameClassName,
  surfaceClassName,
  isDragOver = false,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  header,
  headerClassName,
  hasHeader = Boolean(header),
  menu,
  promptEditor,
  images = EMPTY_IMAGES,
  files = EMPTY_FILES,
  nonPersistedImageIds = EMPTY_IMAGE_ID_SET,
  onExpandImage,
  onRemoveImage,
  onRemoveFile,
  footerLeft,
  footerLeftClassName,
  footerRight,
  stashShoulder,
  hasStashShoulder = false,
  onStashShortcut,
  keybindings,
  terminalOpen = false,
  footerCompact = false,
  primaryActionsCompact = false,
  resting = false,
  contextStrip,
}: ComposerSurfaceProps) {
  const commandPaletteOpen = useCommandPaletteStore((state) => state.open);
  const hasFooterLeft = footerLeft !== null && footerLeft !== undefined;
  const hasContextStrip = contextStrip !== null && contextStrip !== undefined;
  const { className: promptEditorClassName, ...promptEditorProps } = promptEditor;
  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className="mx-auto w-full min-w-0 max-w-3xl"
      data-chat-composer-form="true"
      // The layout states a test or a stylesheet needs to see, read off the one
      // element that exists in every Composer state. Class names change with
      // every Tailwind pass; these do not.
      data-composer-resting={String(resting)}
      data-footer-compact={String(footerCompact)}
      data-primary-actions-compact={String(primaryActionsCompact)}
      onKeyDownCapture={(event) => {
        if (!onStashShortcut || !keybindings || commandPaletteOpen || event.defaultPrevented)
          return;
        const command = resolveShortcutCommand(event.nativeEvent, keybindings, {
          context: { terminalFocus: isTerminalFocused(), terminalOpen },
        });
        if (command !== "composer.stash") return;
        event.preventDefault();
        event.stopPropagation();
        onStashShortcut();
      }}
    >
      {hasStashShoulder ? (
        <ComposerBanner.Dock className="pointer-events-none relative z-20 mx-auto flex w-[calc(100%-2*var(--chat-composer-drawer-inset))] items-end">
          <div className="pointer-events-auto ml-auto">{stashShoulder}</div>
        </ComposerBanner.Dock>
      ) : null}
      {/* The shell paints glass; the host owns the outline and focus ring. */}
      <div
        className={cn(
          "composer-glass-shell group relative rounded-[22px] transition-colors duration-200",
          hasContextStrip && "composer-glass-shell-with-context",
        )}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div
          className={cn(
            // Transparent, not `bg-card`: an opaque fill here would hide the
            // shell's backdrop blur and the composer would stop reading as glass.
            // State emphasis moves to rings so it stacks over the ::after outline
            // instead of fighting it for the same border box.
            // `z-10`: the strip below is `z-0`, and without this the strip —
            // later in source order — would paint over the composer's own edge.
            "composer-glass-host relative z-10 rounded-[22px] bg-transparent transition-colors duration-200 has-focus-visible:ring-1 has-focus-visible:ring-ring/45",
            isDragOver ? "bg-accent/30 ring-1 ring-primary/70" : "",
          )}
        >
          <div className={cn("rounded-[22px] p-px transition-colors duration-200", frameClassName)}>
            <div
              // The box the Resting Composer turns into a single row.
              data-chat-composer-surface="true"
              className={cn(
                "rounded-[20px] transition-[background-color] duration-200",
                surfaceClassName,
              )}
            >
              {/* `hasHeader`, not just `header`. A banner element is truthy
                  even in the states where it renders null, so gating on the
                  prop alone drew this box empty: a 1px ruled strip above the
                  prompt on every thread with nothing to announce. The caller
                  decides from the same facts the banner does. */}
              {hasHeader && header ? (
                <div
                  data-chat-composer-header="true"
                  className={cn(
                    "rounded-t-[19px] border-b border-border/65 bg-muted/20",
                    headerClassName,
                  )}
                >
                  {header}
                </div>
              ) : null}

              <div
                // The box the Resting Composer's rules in `index.css` shrink.
                data-chat-composer-body="true"
                className={cn(
                  "relative px-3 pb-2 sm:px-4",
                  hasHeader ? "pt-2.5 sm:pt-3" : "pt-3.5 sm:pt-4",
                )}
              >
                {menu.open ? (
                  <div className="absolute inset-x-0 bottom-full z-20 mb-2 px-1">
                    <ComposerCommandMenu
                      items={[...(menu.items ?? [])]}
                      resolvedTheme={menu.resolvedTheme}
                      isLoading={menu.isLoading ?? false}
                      triggerKind={menu.triggerKind ?? null}
                      groupSlashCommandSections={menu.groupSlashCommandSections ?? false}
                      emptyStateText={menu.emptyStateText ?? "No results"}
                      activeItemId={menu.activeItemId ?? null}
                      onHighlightedItemChange={menu.onHighlightedItemChange ?? (() => {})}
                      onSelect={menu.onSelect ?? (() => {})}
                    />
                  </div>
                ) : null}

                <ComposerImageTray
                  images={images}
                  files={files}
                  nonPersistedImageIds={nonPersistedImageIds}
                  onExpandImage={onExpandImage}
                  onRemoveImage={onRemoveImage}
                  onRemoveFile={onRemoveFile}
                  resting={resting}
                />

                <ComposerPromptEditor
                  {...promptEditorProps}
                  disabled={promptEditorProps.disabled ?? false}
                  onPaste={promptEditorProps.onPaste ?? noopPaste}
                  {...(promptEditorClassName ? { className: promptEditorClassName } : {})}
                />
              </div>

              <div
                data-chat-composer-footer="true"
                data-chat-composer-footer-compact={footerCompact ? "true" : "false"}
                className={cn(
                  "flex min-w-0 flex-nowrap items-center overflow-visible px-2.5 pb-2.5 sm:px-3 sm:pb-3",
                  hasFooterLeft ? "justify-between" : "justify-end",
                  footerCompact ? "gap-[8px] md:gap-1.5" : "gap-[8px] md:gap-0",
                )}
              >
                {hasFooterLeft ? (
                  <div
                    data-chat-composer-icon-dock="true"
                    // No fill and no rounding: this is a scroll container, not a
                    // surface. A `bg-muted/30 rounded-md` here reads as a second
                    // container sitting in the composer's bottom-left corner, and
                    // it is the reason the two composers looked like different
                    // components — only the new-thread one overrode it away.
                    // `-m-1`/`p-1` cancel, so the extra room is for focus rings.
                    className={cn(
                      "-m-1 flex min-w-0 max-w-full shrink grow-0 items-center gap-1 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                      footerLeftClassName,
                    )}
                  >
                    {footerLeft}
                  </div>
                ) : null}

                <div
                  data-chat-composer-actions="right"
                  data-chat-composer-primary-actions-compact={
                    primaryActionsCompact ? "true" : "false"
                  }
                  className="flex shrink-0 flex-nowrap items-center justify-end gap-[8px]"
                >
                  {footerRight}
                </div>
              </div>
            </div>
          </div>
        </div>

        {contextStrip ? (
          <div
            className="composer-context-strip relative z-0 -mt-4 mx-auto flex h-[calc(1rem+var(--composer-glass-context-extension,2rem))] w-[calc(100%-2*var(--composer-glass-drawer-inset))] items-center gap-2 overflow-x-clip overflow-y-visible pt-4 pb-0 ps-1 pe-2"
            data-chat-composer-context-strip="true"
          >
            {contextStrip}
          </div>
        ) : null}
      </div>
    </form>
  );
});
